/**
 * Type definitions for JSON-driven form rendering
 */
// Enhanced validation constants (matching backend)
export const VALIDATION_CONSTANTS = {
    MAX_NAME_LENGTH: 35,
    MAX_EMAIL_LENGTH: 100,
    DEFAULT_TEXT_MAX_LENGTH: 60,
    MAX_EVENT_DATA_SIZE: 10000, // 10KB
    EMAIL_REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
    DANGEROUS_FIELD_NAMES: [
        '__proto__',
        'constructor',
        'prototype',
        'eval',
        'function',
        'script',
        'javascript:',
    ],
};
/**
 * Gets the default value for a field based on its type
 */
const getDefaultValueForType = (type) => {
    switch (type) {
        case 'checkbox':
        case 'dropdown-multi':
            return [];
        case 'termsCheckbox':
            return false;
        case 'reCAPTCHA':
            return '';
        default:
            return '';
    }
};
/**
 * Helper to initialize field values recursively (including children)
 * @param fields - Array of fields to initialize
 * @param values - Object to populate with initialized values
 * @param initialData - Optional initial data to override defaults
 */
const initializeFieldsRecursive = (fields, values, initialData) => {
    for (const field of fields) {
        // Skip divider fields - they don't have values
        if (field.type === 'divider')
            continue;
        // reCAPTCHA fields always use the canonical 'turnstile' key regardless of
        // the schema's fieldName (CDN schemas may omit fieldName or use arbitrary names).
        if (field.type === 'reCAPTCHA') {
            values['turnstile'] = initialData?.['turnstile'] ?? '';
            continue;
        }
        // Hidden fields with a `value` property always use that fixed value
        if (field.type === 'hidden' && field.value !== undefined) {
            values[field.fieldName] = field.value;
        }
        // Priority: initialData > defaultValue > type default
        else if (initialData && field.fieldName in initialData) {
            values[field.fieldName] = initialData[field.fieldName];
        }
        else if (field.defaultValue !== undefined) {
            values[field.fieldName] = field.defaultValue;
        }
        else {
            values[field.fieldName] = getDefaultValueForType(field.type);
        }
        // Initialize child fields if this is a dropdown with options that have children
        if (field.type === 'dropdown' && field.options) {
            for (const option of field.options) {
                if (option.children) {
                    initializeFieldsRecursive(option.children, values, initialData);
                }
            }
        }
    }
};
/**
 * Initializes form values from a schema definition
 * Uses defaultValue from schema if provided, otherwise uses type-appropriate defaults
 * Also initializes conditional child fields
 * @param schema - Form schema definition
 * @param initialData - Optional initial data to override defaults
 * @returns Initialized form values
 */
export const initializeFormValues = (schema, initialData) => {
    const values = {};
    initializeFieldsRecursive(schema.fields, values, initialData);
    return values;
};
/**
 * Validates email format
 */
const isValidEmail = (email) => {
    if (!email || typeof email !== 'string')
        return false;
    const trimmed = email.trim();
    if (trimmed.length > VALIDATION_CONSTANTS.MAX_EMAIL_LENGTH)
        return false;
    return VALIDATION_CONSTANTS.EMAIL_REGEX.test(trimmed);
};
/**
 * Gets error message for a field if invalid
 * @param field - Field definition to validate
 * @param value - Current value of the field
 * @returns Error message string or null if valid
 */
// NOTE: callers must pass values['turnstile'] (not values[field.fieldName]) for reCAPTCHA fields.
export const getFieldError = (field, value) => {
    // If field is not required and empty, it's valid
    if (!field.validation?.required && (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0))) {
        return null;
    }
    if (field.type === 'checkbox' || field.type === 'dropdown-multi') {
        if (!Array.isArray(value) || value.length === 0) {
            return field.validation?.required ? 'Please select at least one option' : null;
        }
        return null;
    }
    else if (field.type === 'termsCheckbox') {
        if (value !== true) {
            return field.validation?.required ? 'You must accept the terms and conditions' : null;
        }
        return null;
    }
    else if (field.type === 'reCAPTCHA') {
        if (field.validation?.required && (!value || value === '')) {
            return 'Please complete the reCAPTCHA verification';
        }
        return null;
    }
    else if (typeof value === 'string') {
        const trimmed = value.trim();
        // Check required
        if (field.validation?.required && trimmed === '') {
            return 'This field is required';
        }
        // Email-specific validation
        if (field.type === 'email' && trimmed !== '') {
            if (!isValidEmail(value)) {
                return 'Please enter a valid email address';
            }
        }
        // Check minLength
        if (field.validation?.minLength !== undefined && trimmed.length < field.validation.minLength) {
            return `Must be at least ${field.validation.minLength} characters`;
        }
        // Check maxLength (schema rule or first/last name constant)
        const maxLen = field.validation?.maxLength ??
            (field.fieldName === 'firstName' || field.fieldName === 'lastName'
                ? VALIDATION_CONSTANTS.MAX_NAME_LENGTH
                : undefined);
        if (maxLen !== undefined && trimmed.length > maxLen) {
            return `Must be no more than ${maxLen} characters`;
        }
        // Check pattern if provided
        if (field.validation?.pattern && trimmed !== '') {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(trimmed)) {
                return 'Invalid format';
            }
        }
        return null;
    }
    else if (typeof value === 'number') {
        // Number validation
        if (field.validation?.min !== undefined && value < field.validation.min) {
            return `Must be at least ${field.validation.min}`;
        }
        if (field.validation?.max !== undefined && value > field.validation.max) {
            return `Must be no more than ${field.validation.max}`;
        }
        return null;
    }
    else {
        if (field.validation?.required && (value === undefined || value === null)) {
            return 'This field is required';
        }
        return null;
    }
};
/**
 * Helper to validate a single field with enhanced validation
 * @param field - Field definition to validate
 * @param value - Current value of the field
 * @returns true if the field is valid
 */
const validateField = (field, value) => {
    // If field is not required and empty, it's valid
    if (!field.validation?.required && (value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0))) {
        return true;
    }
    if (field.type === 'checkbox' || field.type === 'dropdown-multi') {
        return Array.isArray(value) && value.length > 0;
    }
    else if (field.type === 'termsCheckbox') {
        return value === true;
    }
    else if (field.type === 'reCAPTCHA') {
        if (field.validation?.required) {
            return typeof value === 'string' && value !== '';
        }
        return true;
    }
    else if (typeof value === 'string') {
        const trimmed = value.trim();
        // Check required
        if (field.validation?.required && trimmed === '') {
            return false;
        }
        // Email-specific validation
        if (field.type === 'email') {
            return isValidEmail(value);
        }
        // Check minLength
        if (field.validation?.minLength !== undefined && trimmed.length < field.validation.minLength) {
            return false;
        }
        // Check maxLength (schema rule or first/last name constant)
        const maxLen = field.validation?.maxLength ??
            (field.fieldName === 'firstName' || field.fieldName === 'lastName'
                ? VALIDATION_CONSTANTS.MAX_NAME_LENGTH
                : undefined);
        if (maxLen !== undefined && trimmed.length > maxLen) {
            return false;
        }
        // Check pattern if provided
        if (field.validation?.pattern) {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(trimmed)) {
                return false;
            }
        }
        return true;
    }
    else if (typeof value === 'number') {
        // Number validation
        if (field.validation?.min !== undefined && value < field.validation.min) {
            return false;
        }
        if (field.validation?.max !== undefined && value > field.validation.max) {
            return false;
        }
        return true;
    }
    else {
        return value !== undefined && value !== null;
    }
};
/**
 * Helper to validate conditional child fields
 * @param fields - Array of fields to validate (including conditional children)
 * @param values - Form values
 * @returns true if all visible required fields are valid
 */
const validateFieldsRecursive = (fields, values) => {
    for (const field of fields) {
        // reCAPTCHA always uses canonical 'turnstile' key (see initializeFieldsRecursive)
        const fieldValue = field.type === 'reCAPTCHA' ? values['turnstile'] : values[field.fieldName];
        if (!validateField(field, fieldValue)) {
            return false;
        }
        // If this is a dropdown with children, validate visible children
        if (field.type === 'dropdown' && field.options) {
            const selectedValue = values[field.fieldName];
            if (selectedValue) {
                const selectedOption = field.options.find(opt => opt.value === selectedValue);
                if (selectedOption?.children) {
                    // Recursively validate child fields
                    if (!validateFieldsRecursive(selectedOption.children, values)) {
                        return false;
                    }
                }
            }
        }
    }
    return true;
};
/**
 * Validates form values against a schema
 * Handles conditional fields (dropdown children) - only validates visible fields
 * @param values - Form values to validate
 * @param schema - Form schema with validation rules
 * @returns true if all required visible fields are filled
 */
export const validateFormValues = (values, schema) => {
    return validateFieldsRecursive(schema.fields, values);
};
/**
 * Sanitizes form values by removing dangerous field names
 * @param values - Form values to sanitize
 * @returns Sanitized form values
 */
export const sanitizeFormValues = (values) => {
    const sanitized = {};
    for (const [key, value] of Object.entries(values)) {
        // Skip dangerous field names
        const lowerKey = key.toLowerCase();
        if (VALIDATION_CONSTANTS.DANGEROUS_FIELD_NAMES.some(dangerous => lowerKey.includes(dangerous))) {
            console.warn(`Skipping potentially dangerous field: ${key}`);
            continue;
        }
        // Only allow safe data types
        // FormValues type allows string | string[] | boolean, but we check for runtime safety
        const valueType = typeof value;
        if (value === null || valueType === 'string' || valueType === 'number' || valueType === 'boolean') {
            sanitized[key] = value;
        }
        else if (Array.isArray(value)) {
            // For arrays, validate items are primitives
            if (value.every(item => typeof item === 'string' ||
                typeof item === 'number' ||
                typeof item === 'boolean')) {
                sanitized[key] = value;
            }
            else {
                console.warn(`Skipping array with non-primitive items for key: ${key}`);
            }
        }
        else if (valueType === 'object') {
            // Handle plain objects (even though FormValues type doesn't allow them, check at runtime)
            // Use Object.prototype.toString to safely check for plain objects
            if (Object.prototype.toString.call(value) === '[object Object]') {
                sanitized[key] = value;
            }
            else {
                console.warn(`Skipping non-plain object for key: ${key}`);
            }
        }
        else {
            console.warn(`Skipping non-primitive value for key: ${key}`);
        }
    }
    return sanitized;
};
/**
 * Validates the size of eventData (form values) before submission
 * @param values - Form values to check
 * @returns Object with isValid boolean and error message if invalid
 */
export const validateEventDataSize = (values) => {
    try {
        const jsonString = JSON.stringify(values);
        if (jsonString.length > VALIDATION_CONSTANTS.MAX_EVENT_DATA_SIZE) {
            return {
                isValid: false,
                error: `Registration data is too large. Please reduce the amount of data entered.`,
            };
        }
        return { isValid: true };
    }
    catch (error) {
        return {
            isValid: false,
            error: 'Unable to validate registration data.',
        };
    }
};
/**
 * Enhanced validation for registration data before submission
 * Validates email format, name lengths, and eventData size
 * @param values - Form values to validate
 * @param schema - Form schema
 * @returns Object with isValid boolean and array of error messages
 */
export const validateRegistrationData = (values, schema) => {
    const errors = [];
    // Validate form fields
    if (!validateFormValues(values, schema)) {
        errors.push('Please fill in all required fields correctly.');
    }
    // Validate email format if present
    const email = values.email || values.registrantEmail || values.schoolEmail;
    if (email && typeof email === 'string') {
        if (!isValidEmail(email)) {
            errors.push('Please enter a valid email address.');
        }
    }
    // Validate eventData size
    const sizeValidation = validateEventDataSize(values);
    if (!sizeValidation.isValid) {
        errors.push(sizeValidation.error || 'Registration data is too large.');
    }
    return {
        isValid: errors.length === 0,
        errors,
    };
};
